/* eslint-disable max-len */
import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('product-variant-selector')
class ProductVariantSelector extends LitElement {
  @property({ type: String, attribute: 'threshold-message-template' })
  accessor thresholdMessageTemplate;

  @property({ type: Number, attribute: 'inventory-threshold' })
  accessor inventoryThreshold;

  constructor() {
    super();
    this.sizeSelected = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.selectedVariantText = this.querySelector(
      '[data-selected-variant-text]',
    );
    this.inventoryThresholdElement = this.querySelector(
      '[data-inventory-threshold-message]',
    );
    this._initQuantity();
    this._handleInitialVariant();
  }

  _handleInitialVariant = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const variant = urlParams.get('variant');
    const selectedVariant = this.querySelector(
      `[data-variant][value="${variant}"]`,
    );
    if (selectedVariant) {
      setTimeout(() => {
        const event = new Event('change', {
          bubbles: true,
          composed: true,
        });
        selectedVariant.checked = true;
        selectedVariant.dispatchEvent(event);
      });
    }
  };

  _initQuantity = () => {
    this.quantity = 1;

    this.quantityInput = this.querySelector('[data-quantity-input]');
    if (!this.quantityInput) return;

    this.quantityAvailable = Number(this.quantityInput.max);
    this.quantityInput.addEventListener('change', this._handleQuantityChange);
    this.quantityDecrementButton = this.querySelector(
      '[data-quantity="decrement"]',
    );
    this.quantityDecrementButton.addEventListener(
      'click',
      this._handleQuantityChange,
    );
    this.quantityIncrementButton = this.querySelector(
      '[data-quantity="increment"]',
    );
    this.quantityIncrementButton.addEventListener(
      'click',
      this._handleQuantityChange,
    );

    this.singleVariantInput = this.querySelector('[data-single-variant-input]');
    const { name, value, dataset } = this.singleVariantInput;
    const { optionAvailable } = dataset;
    const eventChange = new CustomEvent('pdp:variant-change', {
      detail: {
        name,
        value,
        optionAvailable: optionAvailable === 'true',
      },
      bubbles: true,
      composed: true,
    });

    // Let product form mount
    setTimeout(() => {
      this.dispatchEvent(eventChange);
    });
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.quantityInput?.removeEventListener(
      'change',
      this._handleQuantityChange,
    );
    this.quantityDecrementButton?.removeEventListener(
      'click',
      this._handleQuantityChange,
    );
    this.quantityIncrementButton?.removeEventListener(
      'click',
      this._handleQuantityChange,
    );
  }

  _handleQuantityChange = (event) => {
    const { target } = event;
    const { value, type } = target;
    const { dataset } = target;
    if (type === 'number') {
      if (value > this.quantityAvailable) {
        this.quantity = this.quantityAvailable;
      } else {
        this.quantity = Number(value);
      }
    }

    if (dataset?.quantity === 'decrement' && this.quantity > 1) {
      this.quantity -= 1;
    }

    if (
      dataset?.quantity === 'increment' &&
      this.quantity < this.quantityAvailable
    ) {
      this.quantity += 1;
      this.quantityDecrementButton.disabled = false;
    }

    this.quantityInput.value = this.quantity;

    this.quantityIncrementButton.disabled =
      this.quantity === this.quantityAvailable;
    this.quantityDecrementButton.disabled = this.quantity === 1;

    const eventChange = new CustomEvent('pdp:quantity-change', {
      detail: {
        quantity: this.quantity,
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(eventChange);
  };

  _handleChange = (event) => {
    this.sizeSelected = true;
    const { target } = event;
    const { value, name, checked, dataset, type } = target;
    if (type === 'number') return;

    const { optionName, optionAvailable, quantityAvailable } = dataset;
    const eventChange = new CustomEvent('pdp:variant-change', {
      detail: {
        name,
        value,
        checked,
        optionAvailable: optionAvailable === 'true',
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(eventChange);
    this.selectedVariantText.innerHTML = optionName;
    this._handleThresholdMessage({
      quantityAvailable: Number(quantityAvailable),
    });
  };

  _handleThresholdMessage = ({ quantityAvailable }) => {
    if (!this.inventoryThresholdElement) return;

    if (quantityAvailable <= this.inventoryThreshold && quantityAvailable > 0) {
      this.inventoryThresholdElement.classList.remove('hidden');
      this.inventoryThresholdElement.innerHTML =
        this.thresholdMessageTemplate.replace('{qty}', quantityAvailable);
    } else {
      this.inventoryThresholdElement.classList.add('hidden');
    }
  };

  render() {
    return html`<slot @change=${this._handleChange}></slot>`;
  }
}

export default ProductVariantSelector;
